import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useThemeStore } from "../../../store";
import { useTheme } from "../../../hooks";
import { themes } from "../../../config/thems";
import { useMemo } from "react";
import Common from "../../../helper/common";
import LottieEmptyState from "./no-data-animation/index";

const SportWiseBookingsRevenue = ({ sportCourtRevenue, height = 350 }) => {
  const { theme: config, setTheme: setConfig } = useThemeStore();
  const { theme: mode } = useTheme();

  const theme = themes.find((theme) => theme.name === config);

  // Parse JSON data
  const parsedData = useMemo(() => {
    if (!sportCourtRevenue) return [];
    try {
      return typeof sportCourtRevenue === "string"
        ? JSON.parse(sportCourtRevenue)
        : sportCourtRevenue;
    } catch {
      return [];
    }
  }, [sportCourtRevenue]);

  // Prepare chart data for Recharts - aggregate by sport
  // const chartData = useMemo(() => {
  //   // Always show these three sports
  //   const targetSports = ["Padel", "Futsal", "Cricket"];
    
  //   // Create a map of sport data from parsed data
  //   const sportDataMap = new Map();
    
  //   if (Array.isArray(parsedData) && parsedData.length > 0) {
  //     parsedData.forEach((sportData) => {
  //       const sport = sportData.sport || "";
  //       const courts = sportData.courts || {};
        
  //       // Calculate total bookings (count of courts)
  //       const totalBookings = Object.keys(courts).length;
        
  //       // Calculate revenue (sum of all court price margins)
  //       const revenue = Object.values(courts).reduce((sum, value) => {
  //         return sum + (parseFloat(value) || 0);
  //       }, 0);

  //       // Only store if it's one of our target sports
  //       if (targetSports.some(s => s.toLowerCase() === sport.toLowerCase())) {
  //         sportDataMap.set(sport.toLowerCase(), {
  //           sport: sport, // Keep original case
  //           totalBookings: totalBookings,
  //           revenue: revenue,
  //           formattedRevenue: Common.Utility.formatNumber(revenue),
  //           formattedBookings: Common.Utility.formatNumber(totalBookings)
  //         });
  //       }
  //     });
  //   }

  //   // Default sample data for sports that don't have data
  //   const defaultData = {
  //     "futsal": {
  //       totalBookings: 45,
  //       revenue: 35000
  //     },
  //     "cricket": {
  //       totalBookings: 38,
  //       revenue: 42000
  //     },
  //     "padel": {
  //       totalBookings: 1000,
  //       revenue: 900
  //     }
  //   };

  //   // Always create entries for all three sports, even if they have no data
  //   const result = targetSports.map((sportName) => {
  //     const existingData = sportDataMap.get(sportName.toLowerCase());
      
  //     if (existingData) {
  //       return existingData;
  //     } else {
  //       // Return sample data for sports that don't exist in the data
  //       const defaultValues = defaultData[sportName.toLowerCase()] || { totalBookings: 0, revenue: 0 };
  //       return {
  //         sport: sportName,
  //         totalBookings: defaultValues.totalBookings,
  //         revenue: defaultValues.revenue,
  //         formattedRevenue: Common.Utility.formatNumber(defaultValues.revenue),
  //         formattedBookings: Common.Utility.formatNumber(defaultValues.totalBookings)
  //       };
  //     }
  //   });

  //   return result;
  // }, [parsedData]);

const chartData = useMemo(() => {
  if (!Array.isArray(parsedData)) return [];

  return parsedData.map((item) => {
    const totalBookings = Number(item.totalBooking || 0);
    const revenue = Number(item.totalRevenue || 0);

    return {
      sport: item.sport,
      totalBookings,
      revenue,
      formattedBookings: Common.Utility.formatNumber(totalBookings),
      formattedRevenue: Common.Utility.formatNumber(revenue),
    };
  });
}, [parsedData]);

  // Colors for different bars
  const colors = [
    `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].primary})`,
    `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].info})`,
    `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].success})`,
    `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].warning})`,
    "#FF6B6B",
    "#6C5CE7",
    "#00B894",
    "#E17055",
  ];

  if (!Array.isArray(chartData) || chartData.length === 0) {
    return (
      <LottieEmptyState
        title="No Sport Data"
        subtitle="No sport data available. Please check your data source."
        height={height}
        width={200}
      />
    );
  }

  // Custom tooltip to show both Total Bookings and Revenue
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: mode === "dark" ? "#1f2937" : "#ffffff",
            border: `1px solid ${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird}`,
            borderRadius: "6px",
            padding: "10px",
          }}
        >
          <p style={{ margin: 0, marginBottom: "5px", fontWeight: 600 }}>
            {label}
          </p>
          <p style={{ margin: 0, color: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].primary})` }}>
            Total Bookings: {data.formattedBookings}
          </p>
          <p style={{ margin: 0, color: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].primary})` }}>
            Revenue: {data.formattedRevenue}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: "100%", height: height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid 
            strokeDasharray="3 3" 
            stroke={`hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird})`}
          />
          <XAxis 
            dataKey="sport"
            tick={{ 
              fontSize: 13, 
              fontWeight: 500,
              fill: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartLabel})`,
              angle: -45,
              textAnchor: 'end',
              height: 60
            }}
            axisLine={{ stroke: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird})` }}
            tickLine={{ stroke: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird})` }}
          />
          <YAxis 
            type="number"
            domain={[0, 'dataMax']}
            tickFormatter={(value) => Common.Utility.formatNumber(value)}
            tick={{ 
              fontSize: 12, 
              fill: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].dark})` 
            }}
            axisLine={{ stroke: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird})` }}
            tickLine={{ stroke: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartGird})` }}
            label={{ 
              value: 'Revenue', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: '13px', fontWeight: 600, fill: `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].chartLabel})` }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar 
            dataKey="revenue" 
            radius={[4, 4, 0, 0]}
            maxBarSize={60}
            fill={`hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].primary})`}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SportWiseBookingsRevenue;

